/**
 * GuestSelector Component
 *
 * Stepper input for selecting the number of guests on a booking.
 * Renders an optional fee info tag: the club's guest fee, or — on a bay that
 * charges per person — that bay's hourly rate for each guest.
 *
 * Fee config is resolved by the parent and passed in as props so this
 * component stays pure and reusable across web/mobile.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/spinbutton/
 */

import type { ComponentPropsWithoutRef } from 'react';
import { cn } from '#utils';

export interface GuestSelectorProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> {
  /** Current guest count (0 = solo booking) */
  guestCount: number;

  /** Called when the user changes the guest count */
  onGuestCountChange: (count: number) => void;

  /**
   * Maximum number of guests allowed.
   * Should be the selected bay's `capacity`. Pass `0` or `undefined` to
   * disable the + button when no bay is selected yet.
   */
  maxGuests?: number;

  /**
   * Whether to show the fee info tag.
   * Pass `true` only when `club.guestFeesEnabled === true`.
   * When `false` all guests are free — no tag is needed.
   */
  showFeeInfo?: boolean;

  /**
   * Number of guests included at no charge.
   * Resolved by parent: userSubscription override → club.guestFeeConfig.freeGuestsPerBooking
   */
  freeGuests?: number;

  /**
   * Cost per guest beyond the free limit, in **cents**.
   * Resolved by parent: userSubscription override → club.guestFeeConfig.feePerGuest
   * e.g. 1000 = $10.00
   */
  feePerGuestCents?: number;

  /**
   * The bay charges each person its hourly rate, in **cents per hour**, and the
   * booking carries no guest fee. When set, the tag shows this rate instead of
   * the free-guest allowance and per-guest fee.
   */
  perPersonRateCents?: number;

  /**
   * What the guests cost is not known yet — the parent is still waiting for the
   * price quote that says whether the bay charges per person. The tag renders a
   * placeholder instead of text, so no price that may not apply is shown.
   */
  feeInfoPending?: boolean;
}

/** Format cents to a compact dollar string, e.g. 1000 → "$10" */
function formatCents(cents: number): string {
  const dollars = cents / 100;
  // Show decimals only when there are non-zero cents (e.g. $10.50)
  return dollars % 1 === 0
    ? `$${dollars.toFixed(0)}`
    : `$${dollars.toFixed(2)}`;
}

/**
 * GuestSelector
 *
 * Renders a labelled stepper (− count +) with an optional info tag describing
 * the club's free-guest allowance and per-guest fee.
 */
export function GuestSelector({
  guestCount,
  onGuestCountChange,
  maxGuests,
  showFeeInfo = false,
  freeGuests = 0,
  feePerGuestCents = 0,
  perPersonRateCents,
  feeInfoPending = false,
  className,
  ...props
}: GuestSelectorProps) {
  const atMin = guestCount <= 0;
  const atMax = maxGuests === undefined || maxGuests <= 0 || guestCount >= maxGuests;

  const decrement = () => {
    if (!atMin) onGuestCountChange(guestCount - 1);
  };

  const increment = () => {
    if (!atMax) onGuestCountChange(guestCount + 1);
  };

  return (
    <div className={cn('space-y-3', className)} {...props}>
      <h3 className="text-xl font-semibold text-butter-gray-900">Add Guests</h3>

      <div className="flex items-center gap-3">
        {/* Stepper pill */}
        <div
          role="group"
          aria-label="Guest count"
          className="flex items-center gap-3 rounded-full border border-butter-gray-100 bg-butter-gray-25 px-3 py-1.5"
        >
          <button
            type="button"
            onClick={decrement}
            disabled={atMin}
            aria-label="Remove a guest"
            className="guest-selector-btn"
            data-action="decrement"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className="h-4 w-4"
            >
              <path
                d="M3 8h10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <output
            aria-live="polite"
            aria-label={`${guestCount} guest${guestCount !== 1 ? 's' : ''}`}
            className="w-6 text-center text-base font-medium tabular-nums text-butter-gray-900"
          >
            {guestCount}
          </output>

          <button
            type="button"
            onClick={increment}
            disabled={atMax}
            aria-label="Add a guest"
            className="guest-selector-btn"
            data-action="increment"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className="h-4 w-4"
            >
              <path
                d="M8 3v10M3 8h10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Fee info tag — rendered when guests cost something, or while that is still unknown */}
        {(showFeeInfo || feeInfoPending) && (
          <p
            aria-busy={feeInfoPending || undefined}
            className="flex items-center gap-1.5 rounded-full border border-butter-gray-100 bg-butter-gray-25 px-3 py-1.5 text-sm text-butter-gray-600"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className="h-4 w-4 flex-shrink-0 text-butter-gray-400"
            >
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.25" />
              <path
                d="M8 7.5v4M8 5.5v.5"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
              />
            </svg>
            {feeInfoPending ? (
              // The Skeleton primitive's look, as a span: a <div> cannot sit inside a <p>.
              <span className="inline-block h-4 w-48 animate-pulse rounded-md bg-surface-3">
                <span className="sr-only">Checking guest pricing</span>
              </span>
            ) : perPersonRateCents !== undefined ? (
              <span>
                Each guest is{' '}
                <strong className="font-semibold text-butter-gray-900">{formatCents(perPersonRateCents)}/hr</strong>
              </span>
            ) : (
              <span>
                {freeGuests > 0 && (
                  <>
                    <strong className="font-semibold text-butter-gray-900">{freeGuests} free</strong>{' '}
                    {freeGuests !== 1 ? 'guests' : 'guest'} included and each additional guest is{' '}
                  </>
                )}
                {freeGuests === 0 && 'Each guest is '}
                <strong className="font-semibold text-butter-gray-900">{formatCents(feePerGuestCents)}</strong>
              </span>
            )}
          </p>
        )}
      </div>

      {/* Max capacity reached notice */}
      {atMax && (
        <p className="flex items-center gap-1.5 text-sm text-amber-700" role="status">
          <svg
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            className="h-4 w-4 flex-shrink-0"
          >
            <path
              d="M8 2L14.5 13H1.5L8 2Z"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeLinejoin="round"
            />
            <path
              d="M8 6.5v3M8 11v.5"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeLinecap="round"
            />
          </svg>
          Maximum capacity of {maxGuests} guest{maxGuests !== 1 ? 's' : ''} reached for this bay
        </p>
      )}
    </div>
  );
}
